import { Moon, Sparkles, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTheme } from 'next-themes';
import { useTranslation } from 'react-i18next';

import { THEMES, type Theme } from '@/config/theme.config';
import { cn } from '@/lib/cn';

/**
 * Ours rather than the lib-components one, for a specific reason: that component
 * cycles through themes on click and has icons for light, dark and system only.
 * This app ships light, dark and CLEAN, so `clean` would fall through to the Sun
 * icon and be indistinguishable from `light` — a three-state control showing two
 * states.
 *
 * Rendered as explicit buttons rather than a cycle. With two themes a toggle is
 * obvious; with three, a cycle makes the user click blind to discover where they
 * land, and never shows which theme is active.
 */
const THEME_ICONS: Record<Theme, typeof Sun> = {
  light: Sun,
  dark: Moon,
  clean: Sparkles,
};

export function ThemeSwitcher({ className }: { className?: string }) {
  const { t } = useTranslation('common');
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  // next-themes resolves on the client; rendering before that would flash the
  // wrong active state.
  useEffect(() => setMounted(true), []);

  if (!mounted) {
    return <div className={cn('h-9 w-[6.75rem] rounded-md border bg-muted/20', className)} />;
  }

  return (
    <div
      role="group"
      aria-label={t('common.theme')}
      className={cn('inline-flex h-9 items-center gap-0.5 rounded-md border bg-background p-0.5', className)}
    >
      {THEMES.map((name) => {
        const Icon = THEME_ICONS[name];
        const active = theme === name;
        return (
          <button
            key={name}
            type="button"
            aria-pressed={active}
            aria-label={t(`themes.${name}`)}
            title={t(`themes.${name}`)}
            onClick={() => setTheme(name)}
            className={cn(
              'inline-flex h-8 w-8 items-center justify-center rounded transition-colors',
              active
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
            )}
          >
            <Icon className="h-4 w-4" aria-hidden />
          </button>
        );
      })}
    </div>
  );
}
